import type {CampaignCopy} from "i18n/en/campaign";
import {useEffect, useRef, type RefObject} from "react";
import {badgeMotionFor, badgeShapeFor, cursorPosition, reversesDirection, springStep, steerAngle, TAG_STEER_MIN_PX, type BadgePoint, type BadgeSpring} from "./campaignBadge";
import type {CampaignId} from "./campaignPlayback";
import {OpenBadge} from "./LandingCampaign.styles";

/** Owns pointer frames separately from artwork playback; hovering never rerenders the canvas. */
export function CampaignCursorBadge({id, active, hitAreaRef, campaign}: {campaign: CampaignCopy; id: CampaignId; active: boolean; hitAreaRef: RefObject<HTMLDivElement | null>}) {
	const badgeRef = useRef<HTMLSpanElement>(null);
	const word = campaign.editions[id].badge;
	const shape = badgeShapeFor(id);
	useEffect(() => {
		const badge = badgeRef.current, hitArea = hitAreaRef.current;
		if (!badge || !hitArea) return;
		const fine = window.matchMedia("(hover: hover) and (pointer: fine)");
		const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
		let frame = 0, target: BadgePoint | null = null, state: BadgeSpring | null = null;
		let previousDelta: BadgePoint | null = null, width = 0, height = 0;
		const resetMotion = () => {
			badge.dataset.rotation = "0";
			badge.style.setProperty("--badge-angle", "0deg");
			delete badge.dataset.bounce;
			badge.dataset.motion = badgeMotionFor(shape, reduced.matches);
		};
		const hide = () => {
			cancelAnimationFrame(frame);
			frame = 0; target = null; state = null; previousDelta = null;
			badge.hidden = true;
			resetMotion();
		};
		const paint = () => {
			frame = 0;
			if (!target || !active || document.hidden || !fine.matches || !hitArea.matches(":hover") || hitArea.closest("[inert]")) { hide(); return; }
			state = badgeMotionFor(shape, reduced.matches) === "instant" || !state
				? {...target, vx: 0, vy: 0} : springStep(state, target);
			badge.hidden = false;
			// Cache the untransformed face once per edition/translation, never layout on every frame.
			if (!width) { width = badge.offsetWidth; height = badge.offsetHeight; }
			const position = cursorPosition(state.x, state.y, window.innerWidth, window.innerHeight, width, height);
			badge.style.transform = `translate3d(${position.x}px, ${position.y}px, 0)`;
			const dx = target.x - position.x, dy = target.y - position.y;
			badge.style.setProperty("--string-length", `${Math.hypot(dx, dy)}px`);
			badge.style.setProperty("--string-angle", `${Math.atan2(dy, dx)}rad`);
			// Continue while visible so an overlay taking hover away cancels even a settled spring.
			frame = requestAnimationFrame(paint);
		};
		const move = (event: PointerEvent) => {
			if (!active || event.pointerType === "touch" || !fine.matches) { hide(); return; }
			const next = {x: event.clientX, y: event.clientY};
			if (target && !reduced.matches) {
				const delta = {x: next.x - target.x, y: next.y - target.y};
				if (shape === "tag") {
					const angle = Math.round(steerAngle(Number(badge.dataset.rotation), delta.x, delta.y) * 100) / 100;
					badge.dataset.rotation = String(angle);
					badge.style.setProperty("--badge-angle", `${angle}deg`);
				}
				if (Math.hypot(delta.x, delta.y) >= TAG_STEER_MIN_PX) {
					if (shape === "blob" && previousDelta && reversesDirection(previousDelta, delta)) {
						badge.dataset.bounce = badge.dataset.bounce === "a" ? "b" : "a";
					}
					previousDelta = delta;
				}
			}
			target = next;
			if (!frame) frame = requestAnimationFrame(paint);
		};
		const keydown = (event: KeyboardEvent) => { if (event.key === "Escape") hide(); };
		hide();
		hitArea.addEventListener("pointermove", move);
		hitArea.addEventListener("pointerleave", hide);
		hitArea.addEventListener("click", hide);
		document.addEventListener("keydown", keydown);
		document.addEventListener("visibilitychange", hide);
		window.addEventListener("blur", hide);
		window.addEventListener("scroll", hide, {passive: true});
		fine.addEventListener("change", hide);
		reduced.addEventListener("change", resetMotion);
		return () => {
			hide();
			hitArea.removeEventListener("pointermove", move);
			hitArea.removeEventListener("pointerleave", hide);
			hitArea.removeEventListener("click", hide);
			document.removeEventListener("keydown", keydown);
			document.removeEventListener("visibilitychange", hide);
			window.removeEventListener("blur", hide);
			window.removeEventListener("scroll", hide);
			fine.removeEventListener("change", hide);
			reduced.removeEventListener("change", resetMotion);
		};
	}, [active, hitAreaRef, shape, id, word]);
	return <OpenBadge ref={badgeRef} $scene={id} data-campaign-badge data-shape={shape} hidden aria-hidden="true">
		<span className="badge-string"/>
		<span className="badge-face">
			<span className="badge-hole"/>
			<span className="badge-dots"><i/><i/><i/></span>
			<span className="badge-word">{word}</span>
			<svg className="badge-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14m-6-6 6 6-6 6"/></svg>
		</span>
	</OpenBadge>;
}
